package org.teddapp.project.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay

/**
 * Petit système de snackbar pour la fonctionnalité Objectifs (spec §8.5).
 *
 * Usage :
 * ```
 *   val snackbar = rememberTeddSnackbarState()
 *   Box(Modifier.fillMaxSize()) {
 *       // ...contenu...
 *       TeddSnackbarHost(state = snackbar)
 *   }
 *   // puis : snackbar.show("Objectif créé !")
 * ```
 *
 * Comportement :
 *  - Affiche le dernier message pendant `durationMs` puis disparaît.
 *  - Un nouveau `show()` remplace le message courant.
 *  - Empilage non géré (volontaire pour cette fonctionnalité — KISS).
 */
class TeddSnackbarState {
    internal var message by mutableStateOf<String?>(null)
        private set
    internal var durationMs by mutableStateOf(2000L)
        private set
    internal var key by mutableStateOf(0)
        private set

    fun show(message: String, durationMs: Long = 2000L) {
        this.message = message
        this.durationMs = durationMs
        this.key += 1
    }

    internal fun clear() {
        message = null
    }
}

@Composable
fun rememberTeddSnackbarState(): TeddSnackbarState = remember { TeddSnackbarState() }

@Composable
fun TeddSnackbarHost(
    state: TeddSnackbarState,
    modifier: Modifier = Modifier,
) {
    LaunchedEffect(state.key) {
        if (state.message != null) {
            delay(state.durationMs)
            state.clear()
        }
    }
    Box(modifier = modifier.fillMaxSize(), contentAlignment = Alignment.BottomCenter) {
        AnimatedVisibility(
            visible = state.message != null,
            enter = fadeIn(animationSpec = tween(200)) +
                slideInVertically(animationSpec = tween(200)) { it / 2 },
            exit = fadeOut(animationSpec = tween(180)) +
                slideOutVertically(animationSpec = tween(180)) { it / 2 },
        ) {
            Box(
                modifier = Modifier
                    .padding(bottom = 28.dp, start = 24.dp, end = 24.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Ink, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = 18.dp, vertical = 12.dp),
            ) {
                Text(
                    text = state.message ?: "",
                    color = TeddTokens.Bg,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Medium,
                )
            }
        }
    }
}
